import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaTimes, FaUsers, FaUser, FaSearch } from 'react-icons/fa';
import { notify } from '../CustomToast';
import videoCallService from '../../services/videoCallService';
import userService from '../../services/userService';
import teamService from '../../services/teamService';
import DatePickerInput from '../Common/DatePickerInput';
import TimePickerInput from '../Common/TimePickerInput';
import { useI18n } from '../../i18n';
import '../Projects/ProjectComposer.css';
import './ScheduleMeetingModal.css';

/* ── Modal ────────────────────────────────────────────────────────── */
const ScheduleMeetingModal = ({ isOpen, onClose, onSuccess }) => {
    const { t } = useI18n();
    const [formData, setFormData] = useState({
        title: '',
        meeting_mode: 'group',
        team_id: '',
        invitees: [],
        settings: { recording: false, screen_share: true },
    });
    const [scheduledDate, setScheduledDate] = useState(null);
    const [scheduledTime, setScheduledTime] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);

    const [teams, setTeams] = useState([]);
    const [teamsLoading, setTeamsLoading] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setFormData({ title: '', meeting_mode: 'group', team_id: '', invitees: [], settings: { recording: false, screen_share: true } });
            setScheduledDate(null);
            setScheduledTime(null);
            setErrors({});
            setLoading(false);
            setSelectedUser(null);
            setSelectedTeam(null);
            setUserSearchQuery('');
            fetchUsers();
            fetchTeams();
        }
    }, [isOpen]);

    const fetchUsers = async () => {
        setUsersLoading(true);
        try {
            const response = await userService.list();
            if (response?.status) setUsers(response.data?.users || []);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        } finally {
            setUsersLoading(false);
        }
    };

    const fetchTeams = async () => {
        setTeamsLoading(true);
        try {
            const response = await teamService.list();
            if (response?.status) setTeams(response.data?.teams || []);
        } catch (err) {
            console.error('Failed to fetch teams:', err);
        } finally {
            setTeamsLoading(false);
        }
    };

    const filteredUsers = users.filter(u =>
        u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(userSearchQuery.toLowerCase())
    );

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.startsWith('settings.')) {
            const key = name.split('.')[1];
            setFormData(prev => ({ ...prev, settings: { ...prev.settings, [key]: checked } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
            if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
            if (name === 'meeting_mode' && value === 'group') setSelectedUser(null);
            if (name === 'meeting_mode' && value === 'one_to_one') setSelectedTeam(null);
        }
    };

    const buildScheduledAt = () => {
        if (!scheduledDate) return '';
        const d = scheduledDate;
        const pad = n => String(n).padStart(2, '0');
        const datePart = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        if (!scheduledTime) return `${datePart}T00:00`;
        const t = scheduledTime;
        return `${datePart}T${pad(t.getHours())}:${pad(t.getMinutes())}`;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setErrors({});
        setLoading(true);

        const payload = {
            ...formData,
            scheduled_at: buildScheduledAt(),
            meeting_type: 'scheduled',
        };
        if (formData.meeting_mode === 'one_to_one' && selectedUser) payload.invitees = [selectedUser.id];
        if (formData.meeting_mode === 'group' && selectedTeam) payload.team_id = selectedTeam.id;
        if (!payload.team_id) delete payload.team_id;
        if (!payload.invitees || payload.invitees.length === 0) delete payload.invitees;

        videoCallService.schedule(payload)
            .then((response) => {
                setLoading(false);
                if (response.status) {
                    notify('success', response.message || t('pages.calendar.schedule.success'));
                    onSuccess(response.data?.meeting);
                    onClose();
                } else {
                    notify('error', response.message || t('pages.calendar.schedule.error'));
                }
            })
            .catch((err) => {
                setLoading(false);
                if (err.status === 422) {
                    setErrors(err.errors || {});
                }
            });
    };

    const handleBackdropClick = (e) => { if (e.target === e.currentTarget) onClose(); };

    if (!isOpen) return null;

    return ReactDOM.createPortal(
        <div className="project-composer-overlay" onClick={handleBackdropClick}>
            <div className="project-composer-modal smm-modal">
                {/* Header */}
                <div className="project-composer-header">
                    <h2 className="project-composer-title">{t('pages.calendar.schedule.title')}</h2>
                    <button className="project-composer-close" onClick={onClose} aria-label={t('common.actions.close')}>
                        <FaTimes size={16} />
                    </button>
                </div>

                {/* Body */}
                <form onSubmit={handleSubmit} noValidate className="smm-form">
                    <div className="project-composer-body">

                        {/* Title */}
                        <div className="project-form-group">
                            <label className="project-form-label">
                                {t('pages.calendar.schedule.meeting_title')} <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                name="title"
                                value={formData.title}
                                onChange={handleChange}
                                placeholder={t('pages.calendar.schedule.title_placeholder')}
                                className={`project-form-input ${errors.title ? 'is-invalid' : ''}`}
                            />
                            {errors.title && <div className="field-error">{errors.title[0]}</div>}
                        </div>

                        {/* Date & Time pickers */}
                        <div className="project-form-row">
                            <div className="project-form-group">
                                <label className="project-form-label">
                                    {t('pages.calendar.schedule.date')} <span className="required">*</span>
                                </label>
                                <DatePickerInput
                                    selected={scheduledDate}
                                    onChange={date => { setScheduledDate(date); setErrors(prev => ({ ...prev, scheduled_at: null })); }}
                                    minDate={new Date()}
                                    placeholder={t('pages.calendar.schedule.date_placeholder')}
                                    isInvalid={!!errors.scheduled_at}
                                />
                            </div>
                            <div className="project-form-group">
                                <label className="project-form-label">
                                    {t('pages.calendar.schedule.time')} <span className="required">*</span>
                                </label>
                                <TimePickerInput
                                    selected={scheduledTime}
                                    onChange={time => { setScheduledTime(time); setErrors(prev => ({ ...prev, scheduled_at: null })); }}
                                    placeholder={t('pages.calendar.schedule.time_placeholder')}
                                    isInvalid={!!errors.scheduled_at}
                                />
                            </div>
                        </div>
                        {errors.scheduled_at && (
                            <div className="field-error smm-datetime-error">
                                {errors.scheduled_at[0]}
                            </div>
                        )}

                        {/* Meeting Mode */}
                        <div className="project-form-group">
                            <label className="project-form-label">{t('pages.calendar.schedule.mode')}</label>
                            <div className="smm-mode-options">
                                <label className={`smm-mode-option ${formData.meeting_mode === 'one_to_one' ? 'smm-mode-option--active' : ''}`}>
                                    <input type="radio" name="meeting_mode" value="one_to_one" checked={formData.meeting_mode === 'one_to_one'} onChange={handleChange} />
                                    <FaUser className="smm-mode-icon" />
                                    <span className="smm-mode-label">{t('pages.calendar.schedule.one_to_one')}</span>
                                </label>
                                <label className={`smm-mode-option ${formData.meeting_mode === 'group' ? 'smm-mode-option--active' : ''}`}>
                                    <input type="radio" name="meeting_mode" value="group" checked={formData.meeting_mode === 'group'} onChange={handleChange} />
                                    <FaUsers className="smm-mode-icon" />
                                    <span className="smm-mode-label">{t('pages.calendar.schedule.group_call')}</span>
                                </label>
                            </div>
                        </div>

                        {/* Team Selection */}
                        {formData.meeting_mode === 'group' && (
                            <div className="project-form-group">
                                <label className="project-form-label">{t('pages.calendar.schedule.select_team')} <span className="required">*</span></label>
                                {selectedTeam ? (
                                    <div className="smm-selected-item">
                                        <div>
                                            <div className="smm-selected-name">{selectedTeam.name}</div>
                                            <div className="smm-selected-sub">{t('pages.calendar.schedule.members_count', { count: selectedTeam.members_count || selectedTeam.members?.length || 0 })}</div>
                                        </div>
                                        <button type="button" className="smm-clear-btn" onClick={() => setSelectedTeam(null)}><FaTimes /></button>
                                    </div>
                                ) : (
                                    <div className="smm-picker-list">
                                        {teamsLoading ? (
                                            <div className="smm-picker-empty">{t('pages.calendar.schedule.loading_teams')}</div>
                                        ) : teams.length === 0 ? (
                                            <div className="smm-picker-empty">{t('pages.calendar.schedule.no_teams')}</div>
                                        ) : teams.map(team => (
                                            <div key={team.id} className="smm-picker-item" onClick={() => setSelectedTeam(team)}>
                                                <div className="smm-team-icon">
                                                    <FaUsers size={16} />
                                                </div>
                                                <div>
                                                    <div className="smm-picker-item-name">{team.name}</div>
                                                    <div className="smm-picker-item-sub">{t('pages.calendar.schedule.members_count', { count: team.members_count || team.members?.length || 0 })}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {errors.team_id && <div className="field-error">{errors.team_id[0]}</div>}
                            </div>
                        )}

                        {/* User Selection */}
                        {formData.meeting_mode === 'one_to_one' && (
                            <div className="project-form-group">
                                <label className="project-form-label">{t('pages.calendar.schedule.select_participant')} <span className="required">*</span></label>
                                {selectedUser ? (
                                    <div className="smm-selected-item">
                                        <div>
                                            <div className="smm-selected-name">{selectedUser.name}</div>
                                            <div className="smm-selected-sub">{selectedUser.email}</div>
                                        </div>
                                        <button type="button" className="smm-clear-btn" onClick={() => setSelectedUser(null)}><FaTimes /></button>
                                    </div>
                                ) : (
                                    <div className="smm-user-picker">
                                        <div className="smm-search-row">
                                            <FaSearch className="smm-search-icon" />
                                            <input
                                                type="text"
                                                value={userSearchQuery}
                                                onChange={e => setUserSearchQuery(e.target.value)}
                                                placeholder={t('pages.calendar.schedule.search_placeholder')}
                                                className="smm-search-input"
                                            />
                                        </div>
                                        <div className="smm-user-list">
                                            {usersLoading ? (
                                                <div className="smm-picker-empty">{t('common.status.loading')}</div>
                                            ) : filteredUsers.length === 0 ? (
                                                <div className="smm-picker-empty">{t('pages.calendar.schedule.no_users')}</div>
                                            ) : filteredUsers.map(user => (
                                                <div key={user.id} className="smm-picker-item" onClick={() => { setSelectedUser(user); setUserSearchQuery(''); }}>
                                                    <div className="smm-user-avatar">
                                                        {user.avatar_url
                                                            ? <img src={user.avatar_url} alt={user.name} />
                                                            : user.name.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div>
                                                        <div className="smm-picker-item-name-sm">{user.name}</div>
                                                        <div className="smm-picker-item-sub">{user.email}</div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {errors.invitees && <div className="field-error">{errors.invitees[0]}</div>}
                            </div>
                        )}

                        {/* Settings */}
                        <div className={`project-form-group smm-settings-group`}>
                            <label className="project-form-label">{t('pages.calendar.schedule.settings')}</label>
                            <div className="smm-settings-options">
                                <label className="smm-settings-option">
                                    <input type="checkbox" name="settings.recording" checked={formData.settings.recording} onChange={handleChange} />
                                    {t('pages.calendar.schedule.enable_recording')}
                                </label>
                                <label className="smm-settings-option">
                                    <input type="checkbox" name="settings.screen_share" checked={formData.settings.screen_share} onChange={handleChange} />
                                    {t('pages.calendar.schedule.allow_screen_share')}
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="project-composer-footer">
                        <button type="button" className="project-composer-btn project-composer-btn-secondary" onClick={onClose} disabled={loading}>{t('common.actions.cancel')}</button>
                        <button type="submit" className="project-composer-btn project-composer-btn-primary" disabled={loading}>
                            {loading ? t('common.status.loading') : t('pages.calendar.schedule.title')}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default ScheduleMeetingModal;
